import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Button, Input, Modal, Spin, message } from "antd";
import { Paperclip } from "@phosphor-icons/react";
import dayjs from "dayjs";
import styled from "styled-components";
import {
  attachWorkItemDocument,
  getMatterWorkItemDocuments,
} from "../../service/workItems";
import {
  InboxModalErrorBanner,
  InboxModalFieldGroup,
  InboxModalHint,
  InboxModalLabel,
} from "../AIPane/AIPaneStyled";
import { border, radius, spacing, surface, text, typography } from "../../theme/tokens";

/**
 * Attach document — the conveyor belt entered from the work item's side
 * (Matthew, 9/9: "when the doc comes back, associate it with the work
 * item and then send over to me for review").
 *
 * The inbox already has the other direction (reading a document → pick
 * the item it answers). Here the reader is standing on the item — on a
 * Home card or the matter's Work tab — and knows the paper has arrived.
 * They see this matter's documents newest first, narrow by name if
 * they need to, pick one, add a word, and confirm. Same doorway move as
 * the inbox button: the link lands in the thread, the item remembers
 * its document, and a Sent item volleys back to the requester's court.
 */

const DocList = styled.div`
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid ${border.light};
  border-radius: ${radius.sm};
  margin-top: ${spacing[1.5]};
`;

const DocOption = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  padding: ${spacing[1.5]} ${spacing[2]};
  background: ${({ $picked }) => ($picked ? "#F1F6FE" : surface.surface)};
  border: none;
  border-bottom: 1px solid ${border.light};
  cursor: pointer;
  font-size: ${typography.size.sm};
  color: ${text.primary};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${({ $picked }) => ($picked ? "#F1F6FE" : surface.hover)};
  }

  small {
    display: block;
    color: ${text.muted};
    font-size: ${typography.size.xs};
  }
`;

const escapeHtml = (value) =>
  value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const toNoteHtml = (textValue) =>
  String(textValue)
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => `<p>${escapeHtml(line)}</p>`)
    .join("");

const arrivedLabel = (iso) => {
  const parsed = dayjs(iso);
  return parsed.isValid() ? parsed.format("M/D/YY") : "";
};

const AttachDocumentModal = ({ open, item, matterId, onClose, onAttached }) => {
  const [query, setQuery] = useState("");
  const [loadStatus, setLoadStatus] = useState("idle");
  const [docs, setDocs] = useState([]);
  const [documentId, setDocumentId] = useState(null);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setDocumentId(null);
    setNote("");
    setError(null);
  }, [open]);

  useEffect(() => {
    if (!open || matterId == null) return undefined;
    let stale = false;
    setLoadStatus("loading");
    const timer = setTimeout(() => {
      getMatterWorkItemDocuments(matterId, { q: query.trim() || undefined })
        .then((response) => {
          if (stale) return;
          setDocs(response?.data?.items || []);
          setLoadStatus("ready");
        })
        .catch(() => {
          if (stale) return;
          setLoadStatus("failed");
        });
    }, query ? 250 : 0);
    return () => {
      stale = true;
      clearTimeout(timer);
    };
  }, [open, matterId, query]);

  const picked = useMemo(
    () => docs.find((doc) => doc.id === documentId) || null,
    [docs, documentId],
  );

  // A Sent item (in the holder's court) comes back to the requester
  // with the paper; a Returned item (already in the requester's court)
  // just gains its document. Say which will happen before the click.
  const willVolley = item?.status === "Sent";

  const canSubmit = documentId != null && !submitting;

  // Work Items v2 (Matthew, 11/2): **Attach** adds the paper and keeps
  // the baton where it is; **Attach & return** is the one-click for a
  // single document and still volleys (or passes on, mid-route).
  const submit = useCallback(
    (andReturn) => {
      if (!canSubmit) return;
      setSubmitting(true);
      setError(null);
      const trimmed = note.trim();
      attachWorkItemDocument(item.id, {
        documentId,
        andReturn,
        ...(trimmed ? { note: toNoteHtml(trimmed) } : {}),
      })
        .then((response) => {
          const volleyed = Boolean(response?.data?.volleyedToRequester);
          const passedTo = response?.data?.passedTo;
          message.success(
            volleyed
              ? `Attached — ${item.itemNumber} is back in ${
                  item.requestedByName ? `${item.requestedByName}'s` : "the requester's"
                } court.`
              : passedTo
              ? `Attached — ${item.itemNumber} passed on to ${passedTo}.`
              : `Attached to ${item.itemNumber}${andReturn ? "" : " — it stays where it is"}.`,
          );
          if (onAttached) onAttached();
          onClose();
        })
        .catch((err) => {
          setError(
            err?.response?.data?.detail ||
              "Couldn't attach the document. Please try again.",
          );
        })
        .finally(() => setSubmitting(false));
    },
    [canSubmit, documentId, item, note, onAttached, onClose],
  );
  const handleSubmit = useCallback(() => submit(true), [submit]);
  const handleAttachOnly = useCallback(() => submit(false), [submit]);

  const title = (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <Paperclip size={16} weight="bold" />
      Attach document to {item?.itemNumber || "work item"}
    </span>
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText={willVolley ? (item?.passToName ? `Attach & pass to ${item.passToName}` : "Attach & return") : "Attach"}
      okButtonProps={{ disabled: !canSubmit, loading: submitting }}
      title={title}
      destroyOnClose
      footer={(_, { OkBtn, CancelBtn }) => (
        <>
          <CancelBtn />
          {willVolley && (
            <Button
              onClick={handleAttachOnly}
              disabled={!canSubmit}
              loading={submitting}
              data-testid="attach-only"
              title="Add the document; the item stays with whoever holds it"
            >
              Attach
            </Button>
          )}
          <OkBtn />
        </>
      )}
    >
      <InboxModalHint style={{ marginBottom: 12 }}>
        {item?.subject}
        {willVolley
          ? " — the document joins the item. Attach keeps it where it is (more to come); Attach & return also sends it on."
          : " — the document link lands in the thread and the item remembers its paper."}
      </InboxModalHint>

      <InboxModalFieldGroup>
        <InboxModalLabel htmlFor="attach-doc-search">
          Which document?
        </InboxModalLabel>
        <Input
          id="attach-doc-search"
          allowClear
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Newest first — type to narrow by name…"
        />
        {loadStatus === "loading" && docs.length === 0 ? (
          <div style={{ textAlign: "center", padding: "16px 0" }}>
            <Spin size="small" />
          </div>
        ) : loadStatus === "failed" ? (
          <InboxModalErrorBanner role="alert">
            Couldn&rsquo;t load this matter&rsquo;s documents.
          </InboxModalErrorBanner>
        ) : docs.length === 0 ? (
          <InboxModalHint style={{ marginTop: 8 }}>
            {query
              ? "No documents on this matter match that."
              : "This matter has no documents yet."}
          </InboxModalHint>
        ) : (
          <DocList role="listbox" aria-label="Documents on this matter">
            {docs.map((doc) => (
              <DocOption
                key={doc.id}
                type="button"
                role="option"
                aria-selected={doc.id === documentId}
                $picked={doc.id === documentId}
                onClick={() => setDocumentId(doc.id)}
              >
                {doc.name}
                <small>
                  {arrivedLabel(doc.created_at)}
                  {doc.folder_name ? ` · ${doc.folder_name}` : ""}
                </small>
              </DocOption>
            ))}
          </DocList>
        )}
      </InboxModalFieldGroup>

      <InboxModalFieldGroup>
        <InboxModalLabel htmlFor="attach-doc-note">Note (optional)</InboxModalLabel>
        <Input.TextArea
          id="attach-doc-note"
          autoSize={{ minRows: 2, maxRows: 5 }}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={
            picked
              ? `e.g. ${picked.name} — signed, came in today.`
              : "e.g. Signed statement came in this morning's mail."
          }
          maxLength={4000}
        />
      </InboxModalFieldGroup>

      {error && <InboxModalErrorBanner role="alert">{error}</InboxModalErrorBanner>}
    </Modal>
  );
};

AttachDocumentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  /** The doorway's item payload (id, itemNumber, subject, status…). */
  item: PropTypes.object,
  matterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func.isRequired,
  onAttached: PropTypes.func,
};

export default AttachDocumentModal;
